import React, { useEffect, useState } from "react";
import api from "../container/helper/Api";

export const extractVimeoId = (value) => {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^\d+$/.test(raw)) return raw;
  if (raw.startsWith("vimeo:") || raw.startsWith("vimeo/")) {
    return extractVimeoId(raw.slice(6));
  }
  const match = raw.match(
    /(?:vimeo-file\/|player\.vimeo\.com\/video\/|vimeo\.com\/(?:video\/)?|\/videos\/)(\d+)/
  );
  return match ? match[1] : null;
};

export const getVimeoEmbedUrl = (value) => {
  if (value == null || value === "") return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (raw.includes("player.vimeo.com/video/")) {
    try {
      const url = new URL(raw);
      url.searchParams.set("title", "0");
      url.searchParams.set("byline", "0");
      url.searchParams.set("portrait", "0");
      return url.toString();
    } catch {
      return raw;
    }
  }
  const id = extractVimeoId(raw);
  return id
    ? `https://player.vimeo.com/video/${id}?title=0&byline=0&portrait=0`
    : null;
};

const checkOembed = async (embedUrl) => {
  const response = await fetch(
    `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(embedUrl)}`
  );
  return response.ok;
};

const fetchVimeoStatus = async (videoId) => {
  if (!videoId) return null;
  try {
    const { data, status } = await api.getVimeoStatus(videoId);
    if (status === 200 && data?.data) return data.data;
  } catch {
    return null;
  }
  return null;
};

const VimeoPlayer = ({
  src,
  style,
  className,
  title = "Video",
  controls = true,
}) => {
  const videoId = extractVimeoId(src);
  const [embedUrl, setEmbedUrl] = useState(getVimeoEmbedUrl(src));
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const initialUrl = getVimeoEmbedUrl(src);
    if (!initialUrl) {
      setEmbedUrl(null);
      setIsReady(false);
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    setEmbedUrl(initialUrl);
    setIsReady(false);

    const check = async () => {
      try {
        const status = await fetchVimeoStatus(videoId);
        const latestUrl = getVimeoEmbedUrl(status?.playerEmbedUrl || initialUrl);
        const oembedReady = latestUrl ? await checkOembed(latestUrl) : false;
        const ready = Boolean(status?.isPlayable || oembedReady);

        if (cancelled) return;
        if (latestUrl) setEmbedUrl(latestUrl);
        if (ready) {
          setIsReady(true);
          return;
        }
      } catch {
        // Keep polling while Vimeo finishes transcoding.
      }

      attempts += 1;
      if (!cancelled && attempts < 90) {
        setTimeout(check, 4000);
      } else if (!cancelled) {
        setIsReady(true);
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [src, videoId]);

  if (embedUrl) {
    if (!isReady) {
      return (
        <div
          className={className}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            minHeight: 160,
            background: "#111",
            color: "#fff",
            textAlign: "center",
            padding: 16,
            ...style,
          }}
        >
          This video is still processing on Vimeo. It will appear here when it
          is ready.
        </div>
      );
    }

    return (
      <iframe
        src={embedUrl}
        className={className}
        style={{ border: 0, width: "100%", height: "100%", ...style }}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        title={title}
      />
    );
  }

  return (
    <video
      src={src}
      className={className}
      style={style}
      controls={controls}
      title={title}
    />
  );
};

export default VimeoPlayer;
